'use client'

import { forwardRef, useCallback, useEffect, useRef } from 'react'
import type { ComponentPropsWithoutRef, ForwardedRef } from 'react'
import { twMerge } from 'tailwind-merge'

export type TopLayerPanelProps = Omit<ComponentPropsWithoutRef<'div'>, 'popover'>

/**
 * Renders its children in the browser top layer via the Popover API (`popover="manual"`), shown on
 * mount and hidden on unmount.
 *
 * Use it for viewport-anchored UI (debug panels, floating widgets, portal containers for floating
 * panels) that must escape an ancestor `transform` or `overflow` and paint above a modal
 * `::backdrop`. A modal `<dialog>` keeps a `transform` for its scale animation, which makes it the
 * containing block for `position: fixed` descendants — so a plain `fixed` child gets pinned to the
 * modal box instead of the viewport, and its `overflow: hidden` clips it. The top layer sidesteps
 * both (its elements are sized against the viewport) without inert-ing the modal the way a nested
 * `showModal()` dialog would. `manual` popovers don't light-dismiss, so visibility is controlled
 * purely by (un)mounting this element.
 *
 * Where it is rendered still matters while a modal `<dialog>` is open: the modal makes everything
 * outside its subtree inert, top layer included. Rendered inside the dialog the panel paints above
 * it and stays interactive; rendered elsewhere it paints above it but cannot be focused or clicked.
 *
 * The base classes reset the UA popover defaults (margin:auto, inset:0, border, padding); pass your
 * own `fixed`/`absolute` placement and surface via `className`. The ref reaches the element, so it
 * can serve as a portal container. On browsers without Popover API support, the attribute is
 * ignored, and it degrades to an in-flow element.
 */
const TopLayerPanel = forwardRef(
    ({ className, ...rest }: TopLayerPanelProps, forwardedRef: ForwardedRef<HTMLDivElement>) => {
        const ref = useRef<HTMLDivElement | null>(null)

        const setRef = useCallback(
            (node: HTMLDivElement | null) => {
                ref.current = node
                if (typeof forwardedRef === 'function') {
                    forwardedRef(node)
                } else if (forwardedRef) {
                    forwardedRef.current = node
                }
            },
            [forwardedRef],
        )

        useEffect(() => {
            const el = ref.current
            if (!el || typeof el.showPopover !== 'function') {
                return
            }
            try {
                el.showPopover()
            } catch {}
            return () => {
                try {
                    if (el.isConnected && el.matches(':popover-open')) {
                        el.hidePopover()
                    }
                } catch {}
            }
        }, [])

        return (
            <div
                ref={setRef}
                //@ts-ignore
                popover="manual"
                // Reset the UA popover defaults (margin:auto, inset:0, border, padding) so the caller's
                // own placement and surface win; `bottom/left-auto` cancels inset:0 for top/right anchors.
                className={twMerge('m-0 border-0 bottom-auto left-auto p-0', className)}
                {...rest}
            />
        )
    },
)

TopLayerPanel.displayName = 'TopLayerPanel'

export default TopLayerPanel
